import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { InlineInputCardWrapper } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { toCssDeclarationName } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/css-declarations';
import { NumberUnitInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/number-unit-input';
import {
  parseSpacing,
  hasImportant,
  stripImportant,
  addImportantIfNeeded,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/parsers';
import { useOnChange } from '@/components/hooks/use-on-change';

export interface CssSpacingInputProps {
  name: string;
  initialValue?: string;
  onChange?: (name: string, value: string) => void;
}

export function CssSpacingInput({
  name,
  initialValue,
  onChange,
}: CssSpacingInputProps) {
  const displayName = useMemo(() => {
    if (name === `margin`) {
      return `Spacing`;
    }
    if (name === `padding`) {
      return `Inner Spacing`;
    }
    return toCssDeclarationName(name);
  }, [name]);

  // Track if initial value had !important
  const [hadImportant] = useState(() => hasImportant(initialValue || ``));
  const cleanInitialValue = useMemo(
    () => stripImportant(initialValue || `1px`),
    [initialValue],
  );

  const {
    top: topDef,
    left: leftDef,
    bottom: bottomDef,
    right: rightDef,
  } = parseSpacing(cleanInitialValue);

  const [top, setTop] = useState(topDef);
  const [left, setLeft] = useState(leftDef);
  const [bottom, setBottom] = useState(bottomDef);
  const [right, setRight] = useState(rightDef);

  const spacing = useMemo(
    () =>
      addImportantIfNeeded(`${top} ${left} ${bottom} ${right}`, hadImportant),
    [top, right, bottom, left, hadImportant],
  );

  useOnChange(() => onChange?.(name, spacing), [spacing]);

  return (
    <InlineInputCardWrapper>
      <span>{displayName}</span>
      <RectangleLayout>
        <div />
        <NumberUnitInput initialValue={topDef} onChange={setTop} />
        <div />
        <NumberUnitInput initialValue={leftDef} onChange={setLeft} />
        <Rectangle />
        <NumberUnitInput initialValue={rightDef} onChange={setRight} />
        <div />
        <NumberUnitInput initialValue={bottomDef} onChange={setBottom} />
        <div />
      </RectangleLayout>
    </InlineInputCardWrapper>
  );
}

const Rectangle = styled.div`
  display: flex;
  width: 100%;
  height: 1.5rem;
  border: 1px solid rgb(228, 234, 237);
  box-shadow: rgba(0, 0, 0, 0.11) 0px 2px 4px 0px,
    rgba(97, 121, 129, 0.19) 0px 4px 13px 0px;
  border-radius: 1rem;
`;

const RectangleLayout = styled.div`
  width: 65%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-content: center;
  justify-items: center;
  align-items: center;
  justify-content: center;
  row-gap: 1rem;
  column-gap: 1.5rem;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 2px 4px 0px,
    rgba(97, 121, 129, 0.08) 0px 4px 13px 0px;
  padding: 1rem 2rem;
  border-radius: 1rem;
`;
